import { useAuth0 } from '@auth0/auth0-react';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { managedLoginSignOut } from '../components/auth/cognito/CognitoManagedLoginProvider';
import { configuration } from '../configuration';

interface AuthUser {
  [key: string]: any;
  email?: string;
  name?: string;
  picture?: string;
  sub?: string;
}

interface UseAuthReturn {
  getAccessTokenSilently: () => Promise<string>;
  isAuthenticated: boolean;
  /**
   * True while the signed-in user's email may still arrive: Cognito reads it
   * from the id token a moment after the session. Once this is false, a
   * missing `user.email` is not coming, so the account can't be shown to be
   * anyone in particular (APP-2441).
   */
  isEmailPending: boolean;
  isLoading: boolean;
  loginWithRedirect: (options?: any) => Promise<void>;
  logout: (options?: any) => Promise<void>;
  user: AuthUser | undefined;
}

function useCognitoAuth(): UseAuthReturn {
  const { authStatus, user: amplifyUser } = useAuthenticator();
  // Fall back to id-token email when signInDetails.loginId is missing
  // (OAuth Hosted-UI sign-ins don't populate signInDetails).
  const [tokenEmail, setTokenEmail] = useState<string | undefined>();
  // Whose id-token read last finished, success or failure. By id, not object:
  // the same user can arrive as a new object, and storing one would re-render.
  const [tokenReadUserId, setTokenReadUserId] = useState<string>();

  useEffect(() => {
    let cancelled = false;

    if (!amplifyUser) {
      setTokenEmail(undefined);

      return;
    }
    fetchAuthSession()
      .then((session) => {
        if (cancelled) return;
        const payload = session.tokens?.idToken?.payload as
          { email?: string } | undefined;

        setTokenEmail(payload?.email);
        setTokenReadUserId(amplifyUser.userId);
      })
      .catch(() => {
        if (cancelled) return;
        setTokenEmail(undefined);
        setTokenReadUserId(amplifyUser.userId);
      });

    return () => {
      cancelled = true;
    };
  }, [amplifyUser]);

  const isAuthenticated = authStatus === 'authenticated';
  const isLoading = authStatus === 'configuring';
  // Amplify reports the session and the user from separate sources, so it can
  // be signed in a moment before the user arrives.
  const isUserLoading = amplifyUser
    ? !amplifyUser.signInDetails?.loginId &&
      tokenReadUserId !== amplifyUser.userId
    : isAuthenticated;

  const user: AuthUser | undefined = useMemo(() => {
    if (!amplifyUser) return undefined;
    const email = amplifyUser.signInDetails?.loginId ?? tokenEmail;

    return {
      email,
      name: email,
      sub: amplifyUser.userId,
    };
  }, [amplifyUser, tokenEmail]);

  const isEmailPending = !user?.email && (isLoading || isUserLoading);

  const getAccessTokenSilently = useCallback(async (): Promise<string> => {
    try {
      const session = await fetchAuthSession();

      return session.tokens?.accessToken?.toString() ?? '';
    } catch {
      return '';
    }
  }, []);

  const loginWithRedirect = useCallback(async () => {
    // Cognito Authenticator handles this natively -- unauthenticated users
    // see the sign-in form. This is a no-op for compatibility.
  }, []);

  const logout = useCallback(
    async (_options?: { logoutParams?: { returnTo?: string } }) => {
      await managedLoginSignOut();
    },
    [],
  );

  return {
    getAccessTokenSilently,
    isAuthenticated,
    isEmailPending,
    isLoading,
    loginWithRedirect,
    logout,
    user,
  };
}

function useAuth0Auth(): UseAuthReturn {
  const auth0 = useAuth0();
  const { getAccessTokenSilently: getAuth0Token } = auth0;

  // auth0-react 2.27 types this as possibly undefined; match the Cognito fallback.
  const getAccessTokenSilently = useCallback(
    async (): Promise<string> => (await getAuth0Token()) ?? '',
    [getAuth0Token],
  );

  return {
    getAccessTokenSilently,
    isAuthenticated: auth0.isAuthenticated,
    isEmailPending: !auth0.user?.email && auth0.isLoading,
    isLoading: auth0.isLoading,
    loginWithRedirect: auth0.loginWithRedirect,
    logout: auth0.logout,
    user: auth0.user as AuthUser | undefined,
  };
}

// COGNITO_ENABLED is a module-level constant (set once at load time from
// configuration.ts). Only one auth provider tree is ever mounted (see
// index.tsx), so only the matching hook's underlying context exists.
// Selecting at module scope satisfies the Rules of Hooks lint rule while
// keeping the call site unconditional. Remove the Auth0 branch once the
// migration is complete.
const useAuthImpl = configuration.COGNITO_ENABLED
  ? useCognitoAuth
  : useAuth0Auth;

export function useAuth(): UseAuthReturn {
  return useAuthImpl();
}
